/**
 * What a repository picker shows for one git account (28.10.2026, owner:
 * "Link a code repository … shows my GrahichDEV repos but not the
 * organisations I own"): the repositories of the user AND of every
 * organisation / group they belong to, merged and de-duplicated, the owners
 * to filter by, and — for an organisation whose repositories cannot be
 * listed — why not (`notices`).
 *
 *   { repos: [repo], owners: [owner], notices: [notice] }
 *
 *   repo   = provider's mapRepo + { ownerKey, ownerAvatar, ownerType }
 *   owner  = { key, login, avatarUrl, type: 'user' | 'org', count }
 *   notice = { kind, owner, url?, secondaryUrl?, scope?, tokenKind? }
 *     kind: 'oauthRestricted' — the org has not approved the OAuth app
 *           'sso'             — the org enforces SAML SSO; `url` authorizes the token
 *           'missingScope'    — the token lacks `scope` (classic PAT / OAuth)
 *           'fineGrained'     — a fine-grained PAT sees one resource owner only
 *           'orgUnavailable'  — the org answered with an error we cannot name
 *
 * Pure helpers; the provider modules do the HTTP.
 */

/** The picker's cap: 500 repositories is plenty (unchanged from before). */
export const MAX_REPOS = 500;

const lower = (s) => String(s || '').toLowerCase();

/** Every list merged in order, de-duplicated by `fullName` (case-insensitive), capped at MAX_REPOS. */
export function mergeRepos(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const repo of list || []) {
      const key = lower(repo?.fullName);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(repo);
      if (out.length >= MAX_REPOS) return out;
    }
  }
  return out;
}

/** Most recently pushed / active first (ISO strings sort as text). */
export function newestFirst(a, b) {
  return String(b.pushedAt || b.updatedAt || '').localeCompare(String(a.pushedAt || a.updatedAt || ''));
}

/**
 * The owners to filter by: the signed-in user first, then organisations A→Z.
 * `extra` adds owners with no visible repository (an org whose repos are
 * hidden still belongs in the filter, next to its notice).
 */
export function ownersOf(repos, { login = '', avatarUrl = '', extra = [] } = {}) {
  const byKey = new Map();
  const add = (o) => {
    const key = lower(o.key || o.login);
    if (!key) return null;
    let entry = byKey.get(key);
    if (!entry) {
      entry = { key, login: o.login || o.key, avatarUrl: o.avatarUrl || '', type: o.type || 'org', count: 0 };
      byKey.set(key, entry);
    } else if (!entry.avatarUrl && o.avatarUrl) {
      entry.avatarUrl = o.avatarUrl;
    }
    return entry;
  };
  if (login) add({ key: login, login, avatarUrl, type: 'user' });
  for (const repo of repos || []) {
    const entry = add({ key: repo.ownerKey || repo.owner, login: repo.ownerKey || repo.owner, avatarUrl: repo.ownerAvatar, type: repo.ownerType });
    if (entry) entry.count += 1;
  }
  for (const o of extra || []) add(o);
  const self = lower(login);
  return [...byKey.values()].sort((a, b) => {
    if (a.key === self) return -1;
    if (b.key === self) return 1;
    return a.login.localeCompare(b.login, undefined, { sensitivity: 'base' });
  });
}

/** Repositories of one owner ('all' or '' = every owner). */
export function filterByOwner(repos, ownerKey) {
  if (!ownerKey || ownerKey === 'all') return repos || [];
  const key = lower(ownerKey);
  return (repos || []).filter((r) => lower(r.ownerKey || r.owner) === key);
}

/** A plain list (a provider without a catalog) as a catalog. */
export function catalogFromList(list, account = {}) {
  const repos = Array.isArray(list) ? list : [];
  return { repos, owners: ownersOf(repos, account), notices: [] };
}
